import { csvMoney, csvRate } from '../csvValues';

import type { CardResult } from './model';

export type ComparisonRow = {
  label: string;
  sales: Record<string, number | null>;
  changes: Record<string, number | null>;
};
export type ComparisonData = { months: Array<string>; rows: Array<ComparisonRow>; total: ComparisonRow };

const valueOf = (value: unknown): unknown =>
  value && typeof value === 'object' && 'value' in value ? value.value : value;
const numberOf = (value: unknown): number | null => {
  const raw = valueOf(value);
  if (raw === null || raw === undefined || raw === '') {
    return null;
  }
  const number = Number(raw);
  return Number.isFinite(number) ? number : null;
};

/** Tableau's percent difference from the previous mark; a missing or zero base is Unknown. */
export function percentageDifference(current: number | null, previous: number | null): number | null {
  return current === null || previous === null || previous === 0 ? null : (current - previous) / Math.abs(previous);
}

/**
 * Monthly sales per member with the workbook's change column. Destination and Currency compare each member with its
 * previous month. Product (2) is saved with Rows addressing, so it compares each product with the product above it
 * in the same month, sorted by total sales.
 */
export function comparisonData(result: CardResult, dimension: string): ComparisonData {
  const byLabel = new Map<string, ComparisonRow>();
  const months = new Set<string>();
  for (const source of result.rows) {
    const month = String(valueOf(source.transaction_date_sydt ?? source['month(transaction_date_sydt)']) ?? '').slice(
      0,
      7,
    );
    if (!/^\d{4}-\d{2}$/.test(month)) {
      continue;
    }
    months.add(month);
    const label = String(valueOf(source[dimension]) ?? 'Unknown');
    const row = byLabel.get(label) ?? { label, sales: {}, changes: {} };
    row.sales[month] = numberOf(source.sales_aud);
    byLabel.set(label, row);
  }
  const sortedMonths = [...months].sort();
  const total = (row: ComparisonRow) => Object.values(row.sales).reduce<number>((sum, value) => sum + (value ?? 0), 0);
  const rows = [...byLabel.values()].sort((a, b) => total(b) - total(a) || a.label.localeCompare(b.label));
  rows.forEach((row, index) => {
    sortedMonths.forEach((month, monthIndex) => {
      const previous =
        dimension === 'product' ? rows[index - 1]?.sales[month] : row.sales[sortedMonths[monthIndex - 1]];
      row.changes[month] = percentageDifference(row.sales[month] ?? null, previous ?? null);
    });
  });
  const grandTotal: ComparisonRow = { label: 'Grand total', sales: {}, changes: {} };
  sortedMonths.forEach((month, index) => {
    const known = rows
      .map((row) => row.sales[month])
      .filter((value): value is number => value !== null && value !== undefined);
    grandTotal.sales[month] = known.length ? known.reduce((sum, value) => sum + value, 0) : null;
    grandTotal.changes[month] =
      dimension === 'product'
        ? null
        : percentageDifference(grandTotal.sales[month], grandTotal.sales[sortedMonths[index - 1]] ?? null);
  });
  return { months: sortedMonths, rows, total: grandTotal };
}

/** Every member and month, sales to the cent and change to six places, with the change basis stated. */
export function comparisonCsv(data: ComparisonData, title: string, dimension: string, range: string): string {
  const cell = (value: unknown) => `"${String(value ?? 'Unknown').replace(/"/g, '""')}"`;
  const lines: Array<Array<unknown>> = [
    ['Report', `Product Performance · ${title}`],
    ['Source', 'lux-analytics-prod.reporting.sales'],
    ['Period', range],
    [
      'Change basis',
      dimension === 'product' ? 'Previous product in the same month (sales descending)' : 'Previous month',
    ],
    [dimension, 'Month', 'Sales AUD', 'Change'],
    ...[...data.rows, data.total].flatMap((row) =>
      data.months.map((month) => [row.label, month, csvMoney(row.sales[month]), csvRate(row.changes[month])]),
    ),
  ];
  return '﻿' + lines.map((line) => line.map(cell).join(',')).join('\r\n');
}
